/**
 * Stripe's outstanding-requirement keys, in words a merchant can act on.
 *
 * `requirements.currently_due` arrives as machine keys - `representative.dob.day`,
 * `business_profile.mcc`, `settings.payments.statement_descriptor`. Printed raw
 * they are worse than useless: a merchant reads "mcc" and has no idea what is
 * being asked, then reads three separate date-of-birth lines and believes three
 * different things are missing. The panel has been showing exactly that.
 *
 * Two jobs here, and they are separate on purpose:
 *
 *   1. Collapse. Several keys can describe one thing a person does once.
 *      `dob.day`, `dob.month` and `dob.year` are one date. Five address lines
 *      are one address. A document's `front` and `back` are one photograph of
 *      one card. Anything the merchant settles with a single action becomes a
 *      single item, with the raw keys kept beside it for support.
 *
 *   2. Say it. The wording is compositional - a subject ("your", "your
 *      business's") and a field ("date of birth") - rather than a flat table of
 *      every key Stripe has ever emitted, because a flat table is only complete
 *      until Stripe adds a country. Composition means a key nobody anticipated
 *      still comes out as a sentence, and the raw key travels with it so nothing
 *      is silently lost. Keys whose composed form would be true but useless get
 *      a hand-written sentence instead.
 *
 * Nothing here decides anything. Stripe holds the requirements and Stripe closes
 * them; this only reads the list aloud.
 */

export type RequirementGroup = 'business' | 'person' | 'bank' | 'agreement' | 'documents';

export interface Requirement {
  /** Stable per item, so React keys and de-duplication agree. */
  key: string;
  /** A phrase, capitalised, no full stop. */
  label: string;
  /** Why Stripe wants it, where saying so helps. */
  detail?: string;
  group: RequirementGroup;
  /** Every raw Stripe key folded into this item. These are what you quote in a ticket. */
  raw: string[];
}

export const GROUP_TITLES: Record<RequirementGroup, string> = {
  business: 'About your business',
  person: 'About the people behind it',
  bank: 'Where your money goes',
  agreement: 'Agreements',
  documents: 'Documents to upload',
};

/**
 * The parts of one thing.
 *
 * Ordered so the more specific pattern is tried first: `address_kanji.town` has
 * to be recognised as a kanji address before the plain `address` rule sees it.
 */
const COLLAPSE: Array<[RegExp, string]> = [
  // A date is one date, not three numbers.
  [/\.dob\.(day|month|year)$/, '.dob'],
  // Sides of one card, pages of one file.
  [/\.(document|additional_document)\.(front|back)$/, '.$1'],
  [/^documents\.([a-z0-9_]+)\.files$/, 'documents.$1'],
  /**
   * Ticking a box is one act.
   *
   * Stripe records three separate facts when terms are accepted - when, from
   * where, and with which browser - and asks for all three as separate keys. To
   * the merchant it is one checkbox, and listing it three times reads as three
   * outstanding tasks. `service_agreement` is left alone deliberately: that one
   * is a genuine choice about which agreement applies, not a by-product of
   * accepting.
   */
  [/^tos_acceptance\.(date|ip|user_agent)$/, 'tos_acceptance'],
  [/^settings\.card_issuing\.tos_acceptance\.(date|ip|user_agent)$/, 'settings.card_issuing.tos_acceptance'],
  // Every line of one address, in each of the scripts Stripe supports.
  [/^business_profile\.support_address\.[a-z0-9_]+$/, 'business_profile.support_address'],
  [/\.address_kanji\.[a-z0-9_]+$/, '.address_kanji'],
  [/\.address_kana\.[a-z0-9_]+$/, '.address_kana'],
  [/\.address\.[a-z0-9_]+$/, '.address'],
  [/\.registered_address\.[a-z0-9_]+$/, '.registered_address'],
];

function collapse(key: string): string {
  for (const [pattern, replacement] of COLLAPSE) {
    if (pattern.test(key)) return key.replace(pattern, replacement);
  }
  return key;
}

/**
 * Leaf names, said once.
 *
 * These are the field names Stripe reuses across `individual`, `representative`,
 * `company` and the per-person tokens. The same leaf means the same thing
 * whichever subject carries it, which is what makes composing a sentence from
 * subject plus field safe rather than a guess.
 */
const FIELDS: Record<string, string> = {
  address: 'address',
  address_kana: 'address, in kana',
  address_kanji: 'address, in kanji',
  dob: 'date of birth',
  email: 'email address',
  export_license_id: 'export licence number',
  export_purpose_code: 'export purpose code',
  first_name: 'first name',
  first_name_kana: 'first name, in kana',
  first_name_kanji: 'first name, in kanji',
  full_name_aliases: 'other names',
  gender: 'gender',
  id_number: 'national identification number',
  id_number_secondary: 'second national identification number',
  last_name: 'last name',
  last_name_kana: 'last name, in kana',
  last_name_kanji: 'last name, in kanji',
  maiden_name: 'maiden name',
  name: 'legal name',
  name_kana: 'legal name, in kana',
  name_kanji: 'legal name, in kanji',
  phone: 'phone number',
  political_exposure: 'declaration about holding public office',
  registered_address: 'registered address',
  registration_date: 'date of registration',
  registration_number: 'company registration number',
  'relationship.director': 'confirmation of whether this person is a director',
  'relationship.executive': 'confirmation of whether this person is an executive',
  'relationship.owner': 'confirmation of whether this person is an owner',
  'relationship.percent_ownership': 'share of the business owned',
  'relationship.representative': 'confirmation of who represents the business',
  'relationship.title': 'job title',
  ssn_last_4: 'Social Security number (last four digits)',
  structure: 'legal structure',
  tax_id: 'tax identification number',
  vat_id: 'VAT number',
  'verification.additional_document': 'second identity document',
  'verification.document': 'identity document',
  'verification.proof_of_liveness': 'liveness check',
};

/** Subjects, and how a sentence about each one begins. */
const SUBJECTS: Record<string, { possessive: string; group: RequirementGroup }> = {
  individual: { possessive: 'Your', group: 'person' },
  representative: { possessive: 'Your', group: 'person' },
  company: { possessive: "Your business's", group: 'business' },
  owners: { possessive: "An owner's", group: 'person' },
  directors: { possessive: "A director's", group: 'person' },
  executives: { possessive: "An executive's", group: 'person' },
};

/**
 * Keys that are their own sentence.
 *
 * A key earns a place here when composition would produce something true and
 * unhelpful. "Your business's mcc" is a phrase nobody can act on; "What your
 * business sells" is. The `detail` line exists for the handful where knowing
 * why Stripe is asking changes what a merchant types - the statement descriptor
 * being the clearest case, since a bad one causes chargebacks months later.
 */
const EXACT: Record<string, { label: string; detail?: string; group: RequirementGroup }> = {
  business_type: {
    label: 'Whether you trade as an individual or as a company',
    group: 'business',
  },
  'business_profile.mcc': {
    label: 'What your business sells',
    detail:
      'Stripe asks you to pick the category that describes your trade. It decides which card ' +
      'schemes and payment methods will accept you, so it is worth choosing carefully.',
    group: 'business',
  },
  'business_profile.name': { label: 'The name your business trades under', group: 'business' },
  'business_profile.product_description': {
    label: 'A description of what you sell',
    detail:
      'A sentence or two in plain language. Stripe reads it when deciding whether your account ' +
      'fits the rules of the card schemes.',
    group: 'business',
  },
  'business_profile.url': {
    label: 'Your website address',
    detail:
      'The site your customers buy from. If you do not have one, Stripe will usually accept a ' +
      'social media page or an online listing that shows what you sell and how to reach you.',
    group: 'business',
  },
  'business_profile.support_phone': {
    label: 'A phone number your customers can call',
    group: 'business',
  },
  'business_profile.support_email': {
    label: 'An email address your customers can write to',
    group: 'business',
  },
  'business_profile.support_url': { label: 'A support page for your customers', group: 'business' },
  'business_profile.support_address': {
    label: 'A postal address your customers can write to',
    group: 'business',
  },
  'business_profile.annual_revenue': { label: 'What you expect to turn over in a year', group: 'business' },
  'business_profile.estimated_worker_count': {
    label: 'Roughly how many people work in the business',
    group: 'business',
  },
  external_account: {
    label: 'A bank account to be paid into',
    detail:
      'Stripe pays your takings out to this account. WyoCSPay never sees the account number - it ' +
      'goes from you to Stripe.',
    group: 'bank',
  },
  'individual.address': { label: 'Your home address', group: 'person' },
  'representative.address': { label: 'Your home address', group: 'person' },
  'company.address': { label: "Your business's registered address", group: 'business' },
  'company.owners_provided': {
    label: 'Confirmation that you have listed everyone who owns 25% or more',
    detail:
      'Anti-money-laundering rules require the beneficial owners of a business to be identified. ' +
      'Stripe needs you to say the list is complete, even when there is nobody on it.',
    group: 'person',
  },
  'company.directors_provided': {
    label: 'Confirmation that you have listed every director',
    group: 'person',
  },
  'company.executives_provided': {
    label: 'Confirmation that you have listed every executive',
    group: 'person',
  },
  'company.ownership_declaration.date': {
    label: 'Confirmation that the ownership details are correct',
    group: 'person',
  },
  'company.ownership_exemption_reason': {
    label: 'Why the business does not have to declare its owners',
    group: 'person',
  },
  'company.verification.document': {
    label: 'A document proving the company exists',
    detail: 'Usually a certificate of incorporation, or an extract from the companies register.',
    group: 'documents',
  },
  'settings.payments.statement_descriptor': {
    label: "The name that appears on your customers' card statements",
    detail:
      'Pick something a customer will still recognise weeks later. A descriptor nobody recognises ' +
      'is one of the most common reasons a payment is charged back.',
    group: 'business',
  },
  'settings.payouts.statement_descriptor': {
    label: 'The name that appears on your own bank statement for payouts',
    group: 'bank',
  },
  'settings.card_issuing.tos_acceptance.date': {
    label: "Acceptance of Stripe's card issuing terms",
    group: 'agreement',
  },
  'settings.card_issuing.tos_acceptance.ip': {
    label: "Acceptance of Stripe's card issuing terms",
    group: 'agreement',
  },
  'tos_acceptance.date': {
    label: "Acceptance of Stripe's terms of service",
    detail:
      'Recorded when you agree to them in the form below, along with the date and the address you ' +
      'agreed from. There is nothing to type.',
    group: 'agreement',
  },
  'tos_acceptance.ip': {
    label: "Acceptance of Stripe's terms of service",
    group: 'agreement',
  },
  'tos_acceptance.service_agreement': {
    label: "Which of Stripe's service agreements applies to you",
    group: 'agreement',
  },
};

/** Uploads, which Stripe names by what the file has to prove. */
const DOCUMENTS: Record<string, string> = {
  bank_account_ownership_verification: 'A bank statement showing the account is yours',
  company_license: 'Your trading licence',
  company_memorandum_of_association: 'Your memorandum of association',
  company_ministerial_decree: 'The ministerial decree establishing the company',
  company_registration_verification: 'Proof that the company is registered',
  company_tax_id_verification: 'Proof of the company tax identification number',
  primary_verification: 'Proof of identity',
  proof_of_address: 'Proof of address',
  proof_of_registration: 'Proof that the business is registered',
  proof_of_ultimate_beneficial_ownership: 'Proof of who ultimately owns the business',
  secondary_verification: 'A second proof of identity',
};

function upperFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Last resort, and deliberately not a blank.
 *
 * If Stripe introduces a key tomorrow, a merchant should still read something
 * in their own language rather than a dotted path, and an operator should still
 * find the raw key in the ticket. `business_profile.foo_bar` becomes "Foo bar
 * (business profile)", which is inelegant and is always better than nothing.
 */
function humanise(key: string): string {
  const parts = key.split('.');
  const leaf = parts.pop() ?? key;
  const context = parts.join(' ').replace(/_/g, ' ');
  const words = leaf.replace(/_/g, ' ');
  return context ? `${upperFirst(words)} (${context})` : upperFirst(words);
}

function describeOne(collapsed: string): { label: string; detail?: string; group: RequirementGroup } {
  const exact = EXACT[collapsed];
  if (exact) return exact;

  const document = /^documents\.([a-z0-9_]+)$/.exec(collapsed);
  if (document) {
    return { label: DOCUMENTS[document[1]] ?? humanise(document[1]), group: 'documents' };
  }

  /**
   * A person Stripe identified by token rather than by role.
   *
   * These appear once a company has named its owners and directors: Stripe then
   * asks for the missing pieces per person, as `person_1Ab2C3.dob`. The token
   * stays in the item key so two people missing the same field remain two items,
   * and is never shown - it means nothing to the merchant, who is looking at
   * Stripe's own form where those people are listed by name.
   */
  const person = /^person_[A-Za-z0-9]+\.(.+)$/.exec(collapsed);
  if (person) {
    const field = FIELDS[person[1]];
    return {
      label: field
        ? `${upperFirst(field)} for one of the people on the account`
        : humanise(person[1]),
      group: 'person',
    };
  }

  const owned = /^([a-z_]+)\.(.+)$/.exec(collapsed);
  if (owned) {
    const subject = SUBJECTS[owned[1]];
    const field = FIELDS[owned[2]];
    if (subject && field) return { label: `${subject.possessive} ${field}`, group: subject.group };
    if (subject) {
      return {
        label: `${subject.possessive} ${humanise(owned[2]).toLowerCase()}`,
        group: subject.group,
      };
    }
  }

  const field = FIELDS[collapsed];
  if (field) return { label: upperFirst(field), group: 'business' };

  return { label: humanise(collapsed), group: 'business' };
}

/**
 * The list Stripe is waiting on, as items rather than keys.
 *
 * Stripe's own order is preserved, and the collapse is stable: the first raw key
 * to reach an item decides where that item sits.
 */
export function describeRequirements(keys: readonly string[]): Requirement[] {
  const byKey = new Map<string, Requirement>();

  for (const raw of keys) {
    const key = collapse(raw);
    const existing = byKey.get(key);
    if (existing) {
      existing.raw.push(raw);
      continue;
    }
    const { label, detail, group } = describeOne(key);
    byKey.set(key, { key, label, detail, group, raw: [raw] });
  }

  return [...byKey.values()];
}

/** The same list, grouped, with empty groups dropped. */
export function groupRequirements(
  items: readonly Requirement[],
): Array<{ group: RequirementGroup; title: string; items: Requirement[] }> {
  const order: RequirementGroup[] = ['business', 'person', 'bank', 'documents', 'agreement'];
  return order
    .map((group) => ({
      group,
      title: GROUP_TITLES[group],
      items: items.filter((item) => item.group === group),
    }))
    .filter((section) => section.items.length > 0);
}

/** One line, for places too small for a list - a banner, a tooltip. */
export function summariseRequirements(keys: readonly string[]): string {
  return describeRequirements(keys)
    .map((item) => item.label.charAt(0).toLowerCase() + item.label.slice(1))
    .join(', ');
}
